import { useRef, useState, useEffect } from 'react'
import { useToast } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { api } from '../../../../services/api'
import { useInfoCustomer } from '../../../../hooks/useInfoCustomer'
import { useUser } from '../../../../hooks/useUser'

export function useAprovacaoPiloto() {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const [image, setImage] = useState(null)
  const [preview, setPreview] = useState(false)
  const [loading, setLoading] = useState(false)
  const [canResend, setCanResend] = useState(false)
  const navigate = useNavigate()

  const { id, sequency } = useParams()
  const { codigo_usuario } = useUser()

  const constrains = {
    video: {
      facingMode: { ideal: 'environment' },
    },
    audio: false,
  }

  const toast = useToast({
    position: 'top-right',
    duration: 3000,
  })

  function dataURItoBlob(dataURI) {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ab], { type: mimeString })
  }

  async function openCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constrains)
      videoRef.current.srcObject = stream
    } catch (error) {
      console.error('Error opening the camera:', error)
      toast({
        title: 'Erro ao acessar a câmera',
        status: 'error',
      })
    }
  }

  function captureImage() {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    // Definir as dimensões do canvas para corresponder às dimensões do vídeo
    canvas.width = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight

    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)
    const imageData = canvas.toDataURL('image/png')
    setImage(imageData)
    setPreview(true)

    // Fechar stream da câmera quando a foto for capturada
    const stream = videoRef.current.srcObject
    if (stream) {
      const tracks = stream.getTracks()
      tracks.forEach((track) => track.stop())
    }
  }

  async function sendPhoto(payload) {
    try {
      const response = await api.post('/ordem-producao/aprovar-piloto', payload)
      toast({
        title: 'Piloto Aprovado com Sucesso',
        status: 'success',
      })

      return response
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast({
          title: error.response.data?.erros?.message,
          status: 'warning',
        })
        setLoading(false)
        setCanResend(true)
        return
      }

      toast({
        title: 'Falha ao enviar a Foto',
        status: 'error',
      })
      setLoading(false)
    }
  }

  async function updatePhoto(payload) {
    try {
      const response = await api.post('/ordem-producao/atualizar-foto-piloto', payload)
      toast({
        title: 'Foto atualizada com sucesso',
        status: 'success',
      })
      setLoading(false)
      return response
    } catch (error) {
      console.error(error)
      toast({
        title: 'Erro ao atualizar foto',
        status: 'error',
      })
      setLoading(false)
    }
  }

  async function approvePilot() {
    setLoading(true)

    const blob = dataURItoBlob(image)
    const file = new File([blob], 'captured_image.jpg', { type: blob.type })

    const formData = new FormData()
    formData.append('imagem', file)
    formData.append('nunota', id)
    formData.append('sequencia', sequency)
    formData.append('codusu', codigo_usuario)

    const photoResponse = canResend ? await updatePhoto(formData) : await sendPhoto(formData)

    if (photoResponse && photoResponse.status === 200) {
      navigate(`/status-pedido/${id}/${sequency}`)
    }
  }

  useEffect(() => {
    if (!preview) {
      openCamera()
    }
  }, [preview])

  return {
    videoRef,
    canvasRef,
    image,
    preview,
    loading,
    canResend,
    captureImage,
    approvePilot,
    setPreview,
  }
}
